import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Grid, MenuItem } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startOfDay } from 'date-fns';

import { ReservationStatus, BookedByUserType, VipStatus } from '@lib/state';
import {
  FormTextField,
  useDebouncedEffect,
  FormSelect,
  EnumValueDisplay,
  FormCheckbox,
  useDeviceType,
} from '@lib/common';
import { FilterSection } from 'app/shared';
import { CorporateAccount, RoomRatePlan } from 'app/state';
import { RoomTypeModel } from '@lib/state/api/generated/properties';
import { RatePlanAutocompleteMenu, useRatePlanAutocomplete } from './rate-plan-autocomplete-menu';
import { ClientDeviceType } from '@lib/common/utils';

export interface ReservationFilters {
  start?: Date;
  end?: Date;
  recordNumber?: number;
  recordNumberRange?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  status?: ReservationStatus;
  corporateAccountName?: string;
  planName?: string;
  isFilterByZeroCharges?: boolean;
  bookedBy?: BookedByUserType;
  userId?: string;
  roomTypeId?: string;
  vipStatus?: VipStatus;
  missingCostCenter?: boolean;
}

type ReservationFiltersForm = Omit<ReservationFilters, 'recordNumber' | 'recordNumberRange'> & {
  recordNumber: string;
  recordNumberRange: string;
};

interface ReservationSearchFilterProps {
  filters: ReservationFilters;
  roomTypes: Array<RoomTypeModel>;
  corporateAccounts: Array<CorporateAccount>;
  onChange: (filters: ReservationFilters) => void;
  userId?: string;
  propertyId: string;
}

export const ReservationSearchFilter: React.FC<ReservationSearchFilterProps> = ({
  filters,
  roomTypes,
  corporateAccounts,
  onChange,
  userId,
  propertyId,
}) => {
  const form = useForm<ReservationFiltersForm>({
    defaultValues: {
      ...filters,
      recordNumber: filters.recordNumber ? String(filters.recordNumber) : '',
      recordNumberRange: filters.recordNumberRange ? String(filters.recordNumberRange) : '',
      corporateAccountName: filters.corporateAccountName ? filters.corporateAccountName : '',
      planName: filters.planName ? filters.planName : '',
      missingCostCenter: false,
    },
  });

  const deviceType = useDeviceType();
  const isMobile = useMemo(() => deviceType !== ClientDeviceType.Desktop, [deviceType]);

  const [selectedRatePlan, setSelectedRatePlan] = useState<string | undefined>(filters.planName);

  const setRatePlan = useCallback(
    (ratePlan: RoomRatePlan) => {
      if (selectedRatePlan !== ratePlan.name) {
        setSelectedRatePlan(ratePlan.name);
        form.setValue('planName', ratePlan.name);
      }
    },
    [form, selectedRatePlan]
  );

  useEffect(() => {
    form.register('start');
    form.register('end');
    form.register('bookedBy');
    form.register('roomTypeId');

    // eslint-disable-next-line
  }, []);

  const {
    start,
    end,
    recordNumber,
    recordNumberRange,
    firstName,
    lastName,
    email,
    phone,
    status,
    corporateAccountName,
    planName,
    isFilterByZeroCharges,
    bookedBy,
    roomTypeId,
    vipStatus,
    missingCostCenter,
  } = form.watch({
    nest: true,
  });

  useDebouncedEffect(
    () => {
      onChange({
        start,
        end,
        recordNumber: recordNumber ? Number(recordNumber) : undefined,
        recordNumberRange: recordNumberRange ? Number(recordNumberRange) : undefined,
        firstName,
        lastName,
        email,
        phone,
        status,
        corporateAccountName,
        planName,
        isFilterByZeroCharges,
        bookedBy,
        userId,
        roomTypeId,
        vipStatus,
        missingCostCenter,
      });
    },
    1000,
    [
      start,
      end,
      recordNumber,
      recordNumberRange,
      firstName,
      lastName,
      email,
      phone,
      status,
      corporateAccountName,
      planName,
      isFilterByZeroCharges,
      bookedBy,
      userId,
      roomTypeId,
      vipStatus,
      missingCostCenter,
    ]
  );

  const ratePlanForm = useMemo(() => ({ propertyId, name: planName }), [propertyId, planName]);

  const autocomplete = useRatePlanAutocomplete(ratePlanForm);

  const planNameInputProps = useMemo(() => {
    return { shrink: planName ? true : undefined };
  }, [planName]);

  return (
    <FormContext {...form}>
      <FilterSection isMobile={isMobile} filters={filters}>
        <Grid container spacing={1}>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <DatePicker
              name="start"
              label="Check-In After"
              format="MMMM d"
              value={start ?? null}
              error={!!form.errors.start}
              onChange={value => form.setValue('start', (value && startOfDay(value)) ?? undefined)}
              color="primary"
              InputProps={{ endAdornment: <FontAwesomeIcon icon="calendar-week" /> }}
              size="small"
              clearable
              fullWidth
              autoOk
              data-testid="searchCheckInInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <DatePicker
              name="end"
              label="Check-In Before"
              format="MMMM d"
              value={end ?? null}
              error={!!form.errors.end}
              onChange={value => form.setValue('end', (value && startOfDay(value)) ?? undefined)}
              color="primary"
              InputProps={{ endAdornment: <FontAwesomeIcon icon="calendar-week" /> }}
              size="small"
              clearable
              fullWidth
              autoOk
              data-testid="searchCheckOutInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormTextField
              name="recordNumber"
              label={recordNumberRange ? 'Res # From' : 'Reservation #'}
              fullWidth
              size="small"
              data-testid="searchRecordNumberInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormTextField
              name="recordNumberRange"
              label="Res # To"
              fullWidth
              size="small"
              data-testid="searchRecordNumberRangeInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormSelect
              name="status"
              label="Reservation Status"
              fullWidth
              size="small"
              data-testid="searchStatusSelect"
            >
              <MenuItem>All</MenuItem>
              {Object.values(ReservationStatus).map(x => (
                <MenuItem key={x} value={x} dense>
                  <EnumValueDisplay value={x} disableTypography />
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormSelect
              defaultValue={''}
              name="roomTypeId"
              label="Room Type"
              fullWidth
              data-testid="searchRoomTypeSelect"
            >
              <MenuItem key="all" value="">
                All
              </MenuItem>
              {roomTypes.map(x => (
                <MenuItem key={x.id} value={x.id}>
                  {x.name}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormTextField
              name="firstName"
              label="First Name"
              fullWidth
              size="small"
              data-testid="searchFirstNameInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormTextField
              name="lastName"
              label="Last Name"
              fullWidth
              size="small"
              data-testid="searchLastNameInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormTextField
              name="email"
              label="Email"
              fullWidth
              size="small"
              data-testid="searchEmailInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormTextField
              name="phone"
              label="Phone"
              fullWidth
              size="small"
              data-testid="searchPhoneInput"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={12} xs={12}>
            <FormSelect
              name="corporateAccountName"
              label="Corporate Account"
              defaultValue={filters.corporateAccountName}
              fullWidth
              size="small"
              data-testid="searchCorporateAccountSelect"
            >
              <MenuItem value="">All</MenuItem>
              {corporateAccounts.map(x => (
                <MenuItem key={x.id} value={x.name}>
                  {x.name}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item lg={2} md={4} sm={12} xs={12}>
            <FormSelect
              defaultValue={''}
              name="bookedBy"
              label="Booked By"
              fullWidth
              size="small"
              data-testid="searchBookedBySelect"
            >
              <MenuItem value={''}>Any</MenuItem>
              <MenuItem value={BookedByUserType.You}>Booked By You</MenuItem>
              <MenuItem value={BookedByUserType.Guest}>Booked By Guest</MenuItem>
              <MenuItem value={BookedByUserType.Delegate}>Booked By Delegate</MenuItem>
              <MenuItem value={BookedByUserType.Staff}>Booked By Hotel Staff</MenuItem>
            </FormSelect>
          </Grid>
          <Grid item lg={2} md={4} sm={12} xs={12}>
            <FormTextField
              name="planName"
              label="Rate Plan"
              fullWidth
              size="small"
              data-testid="searchPlanNameSelect"
              InputLabelProps={planNameInputProps}
              {...autocomplete.getInputProps()}
              InputProps={{
                autoComplete: 'off',
                onBlur: () => autocomplete.setOpen(false),
              }}
              placeholder="Type to search..."
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormSelect defaultValue={''} name="vipStatus" label="VIP Status" fullWidth>
              <MenuItem value={undefined}>None</MenuItem>
              {Object.values(VipStatus).map(x => (
                <MenuItem value={x} key={x}>
                  <EnumValueDisplay value={x} />
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormCheckbox
              name="isFilterByZeroCharges"
              label="Free / $0 Reservation"
              fullWidth
              size="small"
              data-testid="searchFreeCheckbox"
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <FormCheckbox
              name="missingCostCenter"
              label="Missing Cost Center"
              fullWidth
              size="small"
              data-testid="searchMissingCostCenterCheckbox"
            />
          </Grid>
        </Grid>
        <RatePlanAutocompleteMenu
          open={autocomplete.open}
          anchorEl={autocomplete.anchorEl}
          matches={autocomplete.matches}
          roomTypes={roomTypes}
          onSelect={(ratePlan: RoomRatePlan) => {
            autocomplete.setOpen(false);
            setRatePlan(ratePlan);
          }}
        />
      </FilterSection>
    </FormContext>
  );
};
